import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import {
  IHistoryAllocationCountry,
  useDeleteAllocationCountryMutation,
  useGetHistoryAllocationCountriesQuery,
  useAllocationCountryCreatedSubscription,
  useAllocationCountryUpdatedSubscription,
  useAllocationCountryDeletedSubscription
} from '../../../../generated/graphql';
import { canUserCreate } from '../../../../utils/rightUtils';
import { Box } from '@mui/material';
import InnerLoader from '../../../Common/InnerLoader';
import DenseTable from '../../../Common/Tables/DenseTable';
import { IHeadCell } from '../../../Common/Tables/OverviewTable';
import { Colors } from '../../../../utils/theme';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../../../Common/Fields/EpraDatePicker';
import CountryForm from './CountryForm';
interface IProps {
  stockId: string | undefined;
}

const AllocationCountryTab: React.FunctionComponent<IProps> = ({ stockId }: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [date, setDate] = useState<Date | null>(null);
  const [minDate, setMinDate] = useState<Date>(new Date());
  const [maxDate, setMaxDate] = useState<Date>(new Date());
  const [allData, setAllData] = useState<IHistoryAllocationCountry[]>([]);
  const canCreate = canUserCreate(state.user, Types.Stock);
  const [fileteredData, setFileteredData] = useState<IHistoryAllocationCountry[]>([]);
  const [openCountryForm, setOpenCountryForm] = useState(false);
  const [allocationCountry, setAllocationCountry] = useState<IHistoryAllocationCountry | null>(
    null
  );
  const [deleteAllocationCountry] = useDeleteAllocationCountryMutation();
  const { data, loading } = useGetHistoryAllocationCountriesQuery({
    fetchPolicy: 'no-cache',
    variables: {
      stock_id: parseInt(stockId as string)
    }
  });
  const createAllocationCountrySubscription = useAllocationCountryCreatedSubscription({
    variables: { stock_id: stockId || '' }
  });
  const updateAllocationCountrySubscription = useAllocationCountryUpdatedSubscription({
    variables: { stock_id: stockId || '' }
  });
  const deleteAllocationCountrySubscription = useAllocationCountryDeletedSubscription({
    variables: { stock_id: stockId || '' }
  });

  useEffect(() => {
    if (data && data.result) {
      const entities = data.result as IHistoryAllocationCountry[];
      setAllData(entities);
      setFileteredData(entities);
    }
  }, [data?.result]);

  useEffect(() => {
    const years = [...new Set(allData.map((r) => r?.year as number))];
    if (allData.length > 0) {
      const min = new Date();
      min.setFullYear(Math.min(...years));
      const max = new Date();
      setMinDate(min);
      max.setFullYear(Math.max(...years));
      setMaxDate(max);
    }
  }, [allData]);

  useEffect(() => {
    if (createAllocationCountrySubscription.data?.result) {
      addAllocationCountry(createAllocationCountrySubscription.data?.result);
    }
    if (updateAllocationCountrySubscription.data?.result) {
      updateAllocationCountry(updateAllocationCountrySubscription.data?.result);
    }
    if (deleteAllocationCountrySubscription.data?.result) {
      removeAllocationCountry(deleteAllocationCountrySubscription.data.result);
    }
  }, [
    createAllocationCountrySubscription.data,
    updateAllocationCountrySubscription.data,
    deleteAllocationCountrySubscription.data
  ]);

  const columns = [
    { title: 'Details', sorting: false, width: 81 },
    {
      title: 'Year',
      field: 'year',
      width: 88,
      type: 'numeric'
    },
    { title: 'Country ', field: 'country.name', width: 1154 },
    { title: 'Country %', field: 'value', type: 'numeric', width: 241 },
    { title: 'Remove', sorting: false, width: 71 }
  ] as IHeadCell[];

  const handleDateChange = (value: unknown) => {
    const input = value as Date | null;
    if (input) {
      if (!Number.isNaN(input.getDate())) {
        setFileteredData(allData.filter((d) => d.year === input.getFullYear()));
      }
    } else {
      setFileteredData(allData);
    }
    setDate(input);
  };

  const handleClose = (
    saved: boolean,
    isNew?: boolean,
    allocationCountryInput?: IHistoryAllocationCountry
  ) => {
    if (saved && allocationCountryInput) {
      if (isNew) {
        addAllocationCountry(allocationCountryInput);
      }
    }

    setAllocationCountry(null);
    setOpenCountryForm(false);
  };

  const handleDetails = (rowData: IHistoryAllocationCountry) => {
    setAllocationCountry(rowData);
    setOpenCountryForm(true);
  };

  const handleRemove = (rowData: IHistoryAllocationCountry) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete allocation country from ${rowData.year.toString()}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const removeAllocationCountry = (input: IHistoryAllocationCountry) => {
    console.log('removeAllocationCountry: ', input);
    const result = (allData as IHistoryAllocationCountry[]).filter((l) => l.id !== input.id);
    setAllData(result);
    setFileteredData(
      (fileteredData as IHistoryAllocationCountry[]).filter((l) => l.id !== input.id)
    );
  };

  const handleDelete = (allocationCountry: IHistoryAllocationCountry) => {
    dispatch({
      type: Actions.HideConfirmation
    });
    deleteAllocationCountry({
      variables: {
        id: allocationCountry.id
      },
      update() {
        removeAllocationCountry(allocationCountry);
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'success',
            text: `Allocation country from ${allocationCountry.year.toString()} deleted`
          }
        });
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: { severity: 'error', text: error.message, autoHide: 6000 }
      })
    );
  };

  const addAllocationCountry = (input: IHistoryAllocationCountry) => {
    console.log('addAllocationCountry: ', input);
    const existing = allData.find((d) => d.id === input.id);
    if (!existing) {
      setAllData([input, ...allData]);
      if (date) {
        if (date.getFullYear() === input.year) {
          setFileteredData([input, ...fileteredData]);
        }
      } else {
        setFileteredData([input, ...fileteredData]);
      }
    }
  };

  const updateAllocationCountry = (input: IHistoryAllocationCountry) => {
    console.log('updateAllocationCountry: ', input);
    setAllData(allData.map((d) => (d.id === input.id ? input : d)));
    if (date) {
      if (date.getFullYear() === input.year) {
        const exists = fileteredData.find((d) => d.id === input.id);
        if (exists) {
          setFileteredData(fileteredData.map((d) => (d.id === input.id ? input : d)));
        } else {
          setFileteredData([input, ...fileteredData]);
        }
      }
    } else {
      setFileteredData(fileteredData.map((d) => (d.id === input.id ? input : d)));
    }
  };

  return (
    <Box>
      {loading ? (
        <InnerLoader />
      ) : (
        <Box className="print\:w-full" sx={{ pt: '25px' }}>
          <Box sx={{ position: 'absolute', pl: '25px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <EpraDatePicker
                id="allocation-country-date-picker"
                label="Year"
                value={date}
                onChange={handleDateChange}
                minDate={minDate}
                maxDate={maxDate}
                views={['year']}
                format="yyyy"
              />
            </LocalizationProvider>
          </Box>
          <DenseTable
            columns={columns}
            data={fileteredData}
            handleDetails={handleDetails}
            handleRemove={handleRemove}
            toolbarBackground={Colors.white}
            createButtonProps={{
              id: 'create-allocation-country-button',
              onClick: () => setOpenCountryForm(true),
              text: 'Create',
              disabled: !canCreate
            }}
            maxHeight={600}
            pageSize={15}
          />
        </Box>
      )}
      {openCountryForm && (
        <CountryForm
          stockId={stockId as string}
          countryInput={allocationCountry}
          handleClose={handleClose}
        />
      )}
    </Box>
  );
};

export default AllocationCountryTab;
